import { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageLoader from "../components/PageLoader";
import StatBox from "../components/league/StatBox";
import TrophiesPanel from "../components/league/TrophiesPanel";
import GwSummaryPanel from "../components/league/GwSummaryPanel";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { fetchArchives, LeagueArchiveSummary } from "../api/archives";
import { fetchMe } from "../api/users";
import { archiveTrophies } from "../lib/trophies";
import { computeArchiveSeasonSummary } from "../lib/archiveSummary";
import { card, widePageContainer, panelHeading } from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";

interface Props {
  user: User;
}

const medal = (rank: number): string | null => (rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null);

export default function ArchivePage({ user }: Props) {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [archive, setArchive] = useState<LeagueArchiveSummary | null>(null);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");

  useBreadcrumbs(archive
    ? [{ label: t("common.home"), to: "/overview" }, { label: archive.name }]
    : [{ label: t("common.home"), to: "/overview" }]);

  useEffect(() => {
    setLoading(true);
    setError("");
    setNotFound(false);
    Promise.all([fetchArchives(user), fetchMe(user).catch(() => null)])
      .then(([archives, me]) => {
        const found = archives.find((a) => a.id === id);
        if (!found) { setNotFound(true); return; }
        setArchive(found);
        setMyUserId(me?.id ?? null);
      })
      .catch(() => setError(t("archivePage.errors.failedToLoad")))
      .finally(() => setLoading(false));
  }, [user, id, t]);

  if (loading) return <PageLoader />;
  if (error) return <div style={{ padding: "2rem", color: "#c00" }}>{error}</div>;
  if (notFound || !archive) {
    return (
      <div style={{ ...widePageContainer, padding: "1.5rem 0" }}>
        <div style={card}>
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>{t("archivePage.errors.notFound")}</p>
          <Link to="/overview" style={{ color: "var(--color-accent)" }}>{t("common.home")}</Link>
        </div>
      </div>
    );
  }

  const { standings } = archive.payload;
  const players = standings.length;
  const topGroup = standings.filter((s) => s.rank === 1);
  const winningScore = topGroup[0]?.score ?? 0;
  const averageScore = players > 0 ? Math.round(standings.reduce((sum, s) => sum + s.score, 0) / players) : 0;
  // Blank when 1st is tied (no single winner to measure a margin from) or there's no runner-up.
  const runnerUpScore = topGroup.length === 1 ? standings.find((s) => s.rank !== 1)?.score : undefined;
  const winningMargin = runnerUpScore !== undefined ? winningScore - runnerUpScore : null;

  const trophies = archiveTrophies(standings);
  const { gwSummary, members } = computeArchiveSeasonSummary(standings);
  const myName = standings.find((s) => s.userId === myUserId)?.displayName ?? null;

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: isMobile ? "1rem 0" : "1.5rem 0" }}>
      {/* ── Header card ── */}
      <div style={{ ...card, marginBottom: "1.25rem", padding: isMobile ? "1rem 1.25rem" : "1.5rem 1.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: isMobile ? "0.35rem" : "0.6rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>
            {archive.competitionName}
          </span>
          <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
          <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>
            {archive.season}
          </span>
          <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
          <span style={{
            display: "inline-flex", alignItems: "center",
            fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
            padding: "1px 6px", borderRadius: 4,
            border: "1px solid var(--color-text-dim)", color: "var(--color-text-dim)",
          }}>
            {archive.modeLabel}
          </span>
          <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
          <span style={{
            fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
            background: "var(--color-surface-alt)", color: "var(--color-text-muted)",
            border: "1px solid var(--color-border)", borderRadius: 4, padding: "1px 5px",
          }}>
            {t("overview.finishedBadge")}
          </span>
        </div>

        <h1 style={{ margin: "0 0 0.3rem", fontFamily: "var(--font-display)", fontSize: isMobile ? "1.4rem" : "2rem", fontWeight: 800, letterSpacing: "0.01em", color: "var(--color-text)", lineHeight: 1.1 }}>
          {archive.name}
        </h1>
        <p style={{ margin: "0 0 1.25rem", fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
          {t("archivePage.finishedOn", { date: new Date(archive.finishedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) })}
        </p>

        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <StatBox label={t("archivePage.statBoxes.players")} value={players} borderColor="var(--color-accent)" />
          <StatBox label={t("archivePage.statBoxes.winningScore")} value={winningScore} borderColor="var(--color-accent)" />
          <StatBox label={t("archivePage.statBoxes.averageScore")} value={averageScore} borderColor="var(--color-accent)" />
          <StatBox label={t("archivePage.statBoxes.winningMargin")} value={winningMargin !== null && winningMargin > 0 ? `+${winningMargin}` : "—"} borderColor="var(--color-accent)" />
        </div>
      </div>

      <GwSummaryPanel gwSummary={gwSummary} members={members} isStarted />
      <TrophiesPanel trophies={trophies} myName={myName} />

      {/* ── Final standings ── */}
      <div style={{ ...card, overflowX: "auto" }}>
        <h2 style={{ ...panelHeading, marginTop: 0 }}>{t("archivePage.standingsTitle")}</h2>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "0.4rem 0.6rem", fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-dim)" }}>{t("archive.table.rank")}</th>
              <th style={{ textAlign: "left", padding: "0.4rem 0.6rem", fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-dim)" }}>{t("archive.table.player")}</th>
              <th style={{ textAlign: "right", padding: "0.4rem 0.6rem", fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-dim)" }}>{t("archive.table.score")}</th>
            </tr>
          </thead>
          <tbody>
            {standings.map((row) => {
              const isMe = row.userId !== null && row.userId === myUserId;
              return (
                <tr key={`${row.userId ?? row.displayName}-${row.rank}`} style={{ borderTop: "1px solid var(--color-border-subtle)", background: isMe ? "color-mix(in srgb, var(--color-accent) 8%, transparent)" : undefined }}>
                  <td style={{ padding: "0.5rem 0.6rem", color: "var(--color-text-muted)" }}>
                    {medal(row.rank) ?? row.rank}{row.isTied ? "=" : ""}
                  </td>
                  <td style={{ padding: "0.5rem 0.6rem", color: isMe ? "var(--color-accent)" : "var(--color-text)", fontWeight: isMe ? 600 : 400 }}>{row.displayName}</td>
                  <td style={{ padding: "0.5rem 0.6rem", textAlign: "right", fontWeight: 600, color: "var(--color-text)" }}>{row.score}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
